import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "pointer-events-none" }

import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'MinimapNavigation',
  setup(__props) {

const scale = ref(1);
const documentObserver = ref<ResizeObserver | null>(null);
const minimapRef = ref<HTMLElement | null>(null);
const scrollPercent = ref(0);
const viewportPercent = ref(0);
const clientWidth = ref(document.documentElement.clientWidth);
const clientHeight = ref(document.documentElement.clientHeight);
const clientScrollHeight = ref(document.documentElement.scrollHeight);

const width = computed(() => {
    return 120 / scale.value;
});

const height = computed(() => {
    return Math.min(300 / scale.value, clientScrollHeight.value * minimapScale.value);
});

const minimapScale = computed(() => {
    const scale = width.value / clientWidth.value;
    return scale;
});

const previewStyle = computed(() => {
    return { transform: `scale(${minimapScale.value || 1})` };
});

const minimapStyle = computed(() => ({
    width: width.value + "px",
    height: height.value + "px",
}));

const viewportIndicatorStyle = computed(() => {
    if (!minimapRef.value) return {};

    const documentHeight = document.documentElement.scrollHeight;

    const minimapHeight = minimapScale.value * documentHeight;

    const indicatorHeight = viewportPercent.value * minimapHeight;
    const topPosition = scrollPercent.value * (minimapHeight - indicatorHeight);

    return {
        height: `${indicatorHeight - 2}px`,
        transform: `translateY(${topPosition}px)`,
    };
});

function updateDimensions() {
    clientWidth.value = document.documentElement.clientWidth;
    clientHeight.value = document.documentElement.clientHeight;
    clientScrollHeight.value = document.documentElement.scrollHeight;
    nextTick(() => {
        updateScrollPercent();
    });
}

function updateScrollPercent() {
    const winScroll = document.documentElement.scrollTop;
    const height = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    scrollPercent.value = winScroll / height;

    const viewportHeight = document.documentElement.clientHeight;
    const documentHeight = document.documentElement.scrollHeight;
    viewportPercent.value = viewportHeight / documentHeight;

    scrollToPercent();
}

function scrollToPercent() {
    if (!minimapRef.value) return;

    const minimapHeight = minimapRef.value.clientHeight;
    const scrollHeight = minimapRef.value.scrollHeight;
    const height = scrollHeight - minimapHeight;

    minimapRef.value.scrollTo(0, height * scrollPercent.value);
}

function handleMinimapClick(e: MouseEvent) {
    if (!minimapRef.value) return;

    const rect = minimapRef.value.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const percentY = clickY / rect.height;

    const documentHeight =
        document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrollTo = documentHeight * percentY;

    window.scrollTo({
        top: scrollTo,
        behavior: "smooth",
    });
}

const handleScaleUpdate = (e: MessageEvent) => {
    if (e.data.type === "scaleUpdate") {
        scale.value = e.data.scale;
        updateScrollPercent();
    }
};

function initResizeObserver() {
    documentObserver.value = new ResizeObserver(() => {
        updateDimensions();
    });

    documentObserver.value.observe(document.documentElement);
}

onMounted(() => {
    initResizeObserver();
    window.addEventListener("message", handleScaleUpdate);
    window.addEventListener("scroll", updateScrollPercent);
    window.addEventListener("resize", updateDimensions);
    updateScrollPercent();
});

onUnmounted(() => {
    documentObserver.value?.disconnect();
    window.removeEventListener("message", handleScaleUpdate);
    window.removeEventListener("scroll", updateScrollPercent);
    window.removeEventListener("resize", updateDimensions);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "minimapRef",
    ref: minimapRef,
    class: "fixed bottom-4 right-4 z-[200] cursor-pointer overflow-hidden rounded-lg border shadow-lg transition-opacity duration-200 hover:opacity-100 md:block",
    style: _normalizeStyle(minimapStyle.value),
    onClick: handleMinimapClick
  }, [
    _createElementVNode("div", {
      class: "absolute left-0 top-0 w-screen origin-top-left pointer-events-none opacity-100",
      style: _normalizeStyle(previewStyle.value)
    }, [
      _createElementVNode("div", _hoisted_1, [
        _renderSlot(_ctx.$slots, "default")
      ])
    ], 4),
    _createElementVNode("div", {
      class: "absolute w-full rounded-lg bg-transparent border border-black opacity-100 transition-transform duration-100 ease-out",
      style: _normalizeStyle(viewportIndicatorStyle.value)
    }, null, 4)
  ], 4))
}
}

})